<template>
  <div class="type-selector">
    <label v-if="label" :for="id" class="selector-label">{{ label }}</label>
    <div class="selector-wrapper" ref="selectorRef">
      <div
        class="selector-input"
        :class="{ 'open': isOpen, 'disabled': disabled }"
        @click="toggleDropdown"
      >
        <div class="selected-value">
          {{ selectedTypeName || $t('invoices.allTypes') }}
        </div>
        <font-awesome-icon
          icon="fa-solid fa-chevron-down"
          class="dropdown-icon"
          :class="{ 'rotate': isOpen }"
        />
      </div>

      <div v-if="isOpen" class="dropdown-menu">
        <div class="dropdown-list">
          <div
            v-for="type in types"
            :key="type.value"
            class="dropdown-item"
            :class="{ 'selected': type.value === selectedType }"
            @click="selectType(type)"
          >
            <div class="type-name">{{ type.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue';
import { useInvoiceTypes } from '@/composables/useCompanyVendor';

const props = defineProps({
  modelValue: {
    type: [String, null],
    default: null
  },
  label: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  id: {
    type: String,
    default: 'type-selector'
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const { types } = useInvoiceTypes();

const isOpen = ref(false);
const selectorRef = ref(null);

const selectedType = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const selectedTypeName = computed(() => {
  if (!types.value || types.value.length === 0) return '';
  const type = types.value.find(t => t.value === selectedType.value);
  return type ? type.label : '';
});

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const selectType = (type) => {
  selectedType.value = type.value;
  emit('change', type.value);
  closeDropdown();
};

const handleClickOutside = (event) => {
  if (selectorRef.value && !selectorRef.value.contains(event.target)) {
    closeDropdown();
  }
};

watch(isOpen, (newValue) => {
  if (newValue) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.type-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selector-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-dark);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.selector-wrapper {
  position: relative;
}

.selector-input {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 40px 10px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 42px;
}

.selector-input:hover:not(.disabled) {
  border-color: var(--gray-light);
}

.selector-input.open {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.type-selector.active-filter .selector-input:not(.open) {
  border: 2px solid #0ea5e9;
  background: rgba(14, 165, 233, 0.05);
}

.selector-input.disabled {
  background: var(--bg-gray);
  cursor: not-allowed;
  opacity: 0.6;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: var(--text-primary);
  background: transparent;
  padding: 0;
}

.search-input::placeholder {
  color: var(--text-light);
}

.selected-value {
  flex: 1;
  font-size: 14px;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-icon {
  position: absolute;
  right: 14px;
  flex-shrink: 0;
  color: var(--text-secondary);
  transition: transform 0.2s ease;
  font-size: 12px;
  pointer-events: none;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 250px;
  background: white;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-list {
  max-height: 250px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid var(--gray-lighter);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover,
.dropdown-item.highlighted {
  background: var(--primary-light);
}

.dropdown-item.selected {
  background: var(--primary-light);
  font-weight: 600;
}

.dropdown-item.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  cursor: default;
  padding: 16px;
}

.dropdown-item.empty:hover {
  background: white;
}

.type-name {
  font-size: 14px;
  color: var(--text-primary);
}
</style>
